import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  Sparkles,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROFESSIONS = [
  "Real estate agent",
  "Mortgage professional",
  "Insurance professional",
  "Financial advisor",
  "Other",
];

const SOURCES = [
  "Referrals",
  "Repeat clients",
  "Search",
  "Social media",
  "Paid ads",
  "Networking",
  "Listings / signs",
  "Other",
];

const SEARCH_RESULTS = [
  { key: "strong", label: "Strong presence — website, profiles, results" },
  { key: "mixed", label: "Mixed — some things show up, some do not" },
  { key: "barely", label: "Barely anything worth clicking" },
  { key: "unsure", label: "I am not sure" },
];

const ASSETS = [
  { key: "brand", label: "Personal brand" },
  { key: "website", label: "Website" },
  { key: "google", label: "Google Business Profile" },
  { key: "content", label: "Content / social" },
  { key: "crm", label: "CRM" },
  { key: "leadcapture", label: "Lead capture" },
  { key: "followup", label: "Follow-up system" },
  { key: "reviews", label: "Reviews" },
];

const IMPROVEMENTS = [
  "Get more inbound leads",
  "Look more professional online",
  "Show up in search when someone looks me up",
  "Build a consistent personal brand",
  "Stop losing leads I already have",
  "Get referral partners something they can send",
];

// Recommend package + services based on what they don't have and how weak search is.
function buildRecommendation({
  business_sources,
  search_result,
  existing_assets,
  improvement_focus,
}) {
  const has = new Set(existing_assets);
  const score = has.size * 12 + (search_result === "strong" ? 15 : search_result === "mixed" ? 8 : 0);
  const gaps = [];
  const strengths = [];

  ASSETS.forEach((a) => {
    (has.has(a.key) ? strengths : gaps).push(a.label);
  });

  // Priority services
  const priorities = [];
  if (!has.has("brand")) priorities.push("Personal brand identity");
  if (!has.has("website")) priorities.push("Website");
  if (!has.has("google")) priorities.push("Google Business Profile");
  if (search_result !== "strong") priorities.push("Local SEO foundations");
  if (!has.has("leadcapture")) priorities.push("Lead capture");
  if (!has.has("followup") || !has.has("crm"))
    priorities.push("CRM & follow-up automation");
  if (!has.has("reviews")) priorities.push("Reviews system");
  if (!has.has("content")) priorities.push("Content system");

  // Package
  let pkg = "Foundation";
  if (has.size >= 5 && search_result === "strong") pkg = "Managed";
  else if (has.size >= 3) pkg = "Growth";

  // Headline diagnosis
  let headline = "You have a starting point. The core presence is missing.";
  if (score >= 60)
    headline = "Your presence is in good shape. Now compound it.";
  else if (score >= 35)
    headline = "You have momentum. A few upgrades will close the gap.";

  return {
    score: Math.min(100, score),
    strengths,
    gaps,
    priorities: priorities.slice(0, 6),
    recommended_package: pkg,
    recommended_services: priorities.slice(0, 4),
    headline,
    improvement_focus,
    business_sources,
  };
}

const STEPS = ["profession", "sources", "search", "assets", "focus", "email", "result"];

export default function GrowthPlan() {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    profession: "",
    business_sources: [],
    search_result: "",
    existing_assets: [],
    improvement_focus: "",
    email: "",
    name: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const setAns = (patch) => setAnswers((a) => ({ ...a, ...patch }));

  const canNext = useMemo(() => {
    switch (STEPS[step]) {
      case "profession":
        return !!answers.profession;
      case "sources":
        return answers.business_sources.length > 0;
      case "search":
        return !!answers.search_result;
      case "assets":
        return true; // 0 selected is a valid answer
      case "focus":
        return !!answers.improvement_focus;
      case "email":
        return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(answers.email);
      default:
        return true;
    }
  }, [step, answers]);

  const summary = useMemo(
    () =>
      buildRecommendation({
        business_sources: answers.business_sources,
        search_result: answers.search_result,
        existing_assets: answers.existing_assets,
        improvement_focus: answers.improvement_focus,
      }),
    [answers]
  );

  const submitEmail = async () => {
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/diagnostic`, {
        name: answers.name || "",
        email: answers.email,
        profession: answers.profession,
        business_sources: answers.business_sources,
        search_result: answers.search_result,
        existing_assets: answers.existing_assets,
        improvement_focus: answers.improvement_focus,
        summary,
      });
      setStep(STEPS.indexOf("result"));
    } catch (e) {
      setError("Could not submit right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (STEPS[step] === "email") {
      submitEmail();
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div className="bg-[#0a0a0a] min-h-screen">
      {/* Hero */}
      <section className="pt-16 md:pt-24 pb-10 md:pb-14 border-b border-[#27272a] relative overflow-hidden">
        <div className="absolute inset-0 radial-fade" />
        <div className="relative max-w-4xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6">
            <span className="gold-line" />
            Interactive Growth Plan
          </div>
          <MaskedLines
            lines={["Your personal", "marketing diagnostic."]}
            className="font-heading text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white"
          />
          <p className="mt-6 text-lg text-[#a1a1aa] leading-relaxed max-w-2xl">
            Six short questions. A real read on your online presence, the gaps
            costing you the most, and the exact next step for your business.
          </p>
        </div>
      </section>

      {/* Progress bar */}
      <div className="sticky top-20 z-30 bg-[#0a0a0a]/90 backdrop-blur-md border-b border-[#27272a]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-4">
          <div className="flex items-center justify-between text-[10px] tracking-[0.28em] uppercase text-[#71717a] mb-2">
            <span>
              Step {Math.min(step + 1, STEPS.length - 1)} of {STEPS.length - 1}
            </span>
            <span>{Math.round(progress)}%</span>
          </div>
          <div className="h-[2px] w-full bg-[#171717] overflow-hidden">
            <motion.div
              className="h-full bg-[#C8A24B]"
              initial={false}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
        </div>
      </div>

      {/* Steps */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={STEPS[step]}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="min-h-[380px]"
            >
              {STEPS[step] === "profession" && (
                <StepShell
                  eyebrow="Question 01"
                  title="What kind of professional are you?"
                >
                  <OptionsGrid
                    options={PROFESSIONS.map((p) => ({ key: p, label: p }))}
                    selected={answers.profession ? [answers.profession] : []}
                    onToggle={(k) => setAns({ profession: k })}
                    single
                    testIdPrefix="opt-profession"
                  />
                </StepShell>
              )}
              {STEPS[step] === "sources" && (
                <StepShell
                  eyebrow="Question 02"
                  title="Where does most of your business come from today?"
                  subtitle="Select all that apply."
                >
                  <OptionsGrid
                    options={SOURCES.map((s) => ({ key: s, label: s }))}
                    selected={answers.business_sources}
                    onToggle={(k) =>
                      setAns({
                        business_sources: toggle(answers.business_sources, k),
                      })
                    }
                    testIdPrefix="opt-source"
                  />
                </StepShell>
              )}
              {STEPS[step] === "search" && (
                <StepShell
                  eyebrow="Question 03"
                  title="What happens when someone searches your name?"
                >
                  <OptionsList
                    options={SEARCH_RESULTS}
                    selected={
                      answers.search_result ? [answers.search_result] : []
                    }
                    onToggle={(k) => setAns({ search_result: k })}
                    single
                    testIdPrefix="opt-search"
                  />
                </StepShell>
              )}
              {STEPS[step] === "assets" && (
                <StepShell
                  eyebrow="Question 04"
                  title="What do you already have in place?"
                  subtitle="Only tick the ones you would show a prospect today."
                >
                  <OptionsGrid
                    options={ASSETS}
                    selected={answers.existing_assets}
                    onToggle={(k) =>
                      setAns({
                        existing_assets: toggle(answers.existing_assets, k),
                      })
                    }
                    testIdPrefix="opt-asset"
                  />
                </StepShell>
              )}
              {STEPS[step] === "focus" && (
                <StepShell
                  eyebrow="Question 05"
                  title="What are you trying to improve most?"
                >
                  <OptionsList
                    options={IMPROVEMENTS.map((i) => ({ key: i, label: i }))}
                    selected={
                      answers.improvement_focus
                        ? [answers.improvement_focus]
                        : []
                    }
                    onToggle={(k) => setAns({ improvement_focus: k })}
                    single
                    testIdPrefix="opt-focus"
                  />
                </StepShell>
              )}
              {STEPS[step] === "email" && (
                <StepShell
                  eyebrow="One last step"
                  title="Where should we save your growth plan?"
                  subtitle="Your answers unlock the full recommendation on the next screen. We keep it on file for when we talk."
                >
                  <div className="space-y-5 max-w-lg">
                    <label className="block">
                      <span className="block text-[10px] tracking-[0.24em] uppercase text-[#71717a] mb-2">
                        Name (optional)
                      </span>
                      <input
                        data-testid="diagnostic-name"
                        value={answers.name}
                        onChange={(e) => setAns({ name: e.target.value })}
                        placeholder="Your name"
                        className={inputCls}
                      />
                    </label>
                    <label className="block">
                      <span className="block text-[10px] tracking-[0.24em] uppercase text-[#71717a] mb-2">
                        Email <span className="text-[#C8A24B]">*</span>
                      </span>
                      <input
                        data-testid="diagnostic-email"
                        type="email"
                        value={answers.email}
                        onChange={(e) => setAns({ email: e.target.value })}
                        placeholder="you@yourbrokerage.com"
                        className={inputCls}
                        required
                      />
                    </label>
                    {error && (
                      <div
                        data-testid="diagnostic-error"
                        className="text-sm text-[#f87171] border border-[#7f1d1d] bg-[#7f1d1d]/10 p-3 rounded-sm"
                      >
                        {error}
                      </div>
                    )}
                  </div>
                </StepShell>
              )}

              {STEPS[step] === "result" && (
                <ResultView
                  summary={summary}
                  answers={answers}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {STEPS[step] !== "result" && (
            <div className="mt-14 pt-8 border-t border-[#27272a] flex items-center justify-between">
              <button
                onClick={back}
                disabled={step === 0}
                data-testid="diagnostic-back"
                className="text-sm text-[#a1a1aa] hover:text-white flex items-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <button
                onClick={next}
                disabled={!canNext || submitting}
                data-testid="diagnostic-next"
                className="btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Preparing plan
                  </>
                ) : STEPS[step] === "email" ? (
                  <>
                    Show My Growth Plan <ArrowRight size={16} />
                  </>
                ) : (
                  <>
                    Continue <ArrowRight size={16} />
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function toggle(arr, val) {
  return arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
}

function StepShell({ eyebrow, title, subtitle, children }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B] mb-4">
        {eyebrow}
      </div>
      <h2 className="font-heading text-3xl md:text-4xl text-white leading-tight mb-3">
        {title}
      </h2>
      {subtitle && (
        <p className="text-[#a1a1aa] mb-10 max-w-2xl">{subtitle}</p>
      )}
      {!subtitle && <div className="mb-10" />}
      {children}
    </div>
  );
}

function OptionsGrid({ options, selected, onToggle, single, testIdPrefix }) {
  return (
    <div className="grid sm:grid-cols-2 gap-2.5">
      {options.map((o) => {
        const active = selected.includes(o.key);
        return (
          <button
            key={o.key}
            type="button"
            data-testid={`${testIdPrefix}-${o.key}`}
            onClick={() => onToggle(o.key)}
            className={`text-left border rounded-sm px-5 py-4 transition-all ${
              active
                ? "border-[#C8A24B] bg-[#C8A24B]/[0.06] text-white"
                : "border-[#27272a] bg-[#0e0e0e] text-[#d4d4d8] hover:border-[#3f3f46]"
            }`}
          >
            <div className="flex items-start gap-3">
              <span
                className={`mt-1 w-3 h-3 rounded-full border flex-shrink-0 ${
                  active
                    ? "bg-[#C8A24B] border-[#C8A24B]"
                    : "border-[#3f3f46]"
                }`}
              />
              <span className="text-sm md:text-base">{o.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function OptionsList({ options, selected, onToggle, single, testIdPrefix }) {
  return (
    <div className="space-y-2.5">
      {options.map((o) => {
        const active = selected.includes(o.key);
        return (
          <button
            key={o.key}
            type="button"
            data-testid={`${testIdPrefix}-${o.key}`}
            onClick={() => onToggle(o.key)}
            className={`w-full text-left border rounded-sm px-5 py-4 transition-all ${
              active
                ? "border-[#C8A24B] bg-[#C8A24B]/[0.06] text-white"
                : "border-[#27272a] bg-[#0e0e0e] text-[#d4d4d8] hover:border-[#3f3f46]"
            }`}
          >
            <div className="flex items-center gap-3">
              <span
                className={`w-4 h-4 border flex-shrink-0 flex items-center justify-center ${
                  active
                    ? "border-[#C8A24B] bg-[#C8A24B]"
                    : "border-[#3f3f46]"
                }`}
              >
                {active && <Check size={10} className="text-[#0a0a0a]" strokeWidth={3} />}
              </span>
              <span className="text-sm md:text-base">{o.label}</span>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function ResultView({ summary, answers }) {
  return (
    <div data-testid="diagnostic-result">
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B] mb-4 flex items-center gap-2">
        <Sparkles size={12} />
        Your Growth Plan
      </div>
      <h2 className="font-heading text-3xl md:text-5xl text-white leading-[1.05] mb-4">
        {summary.headline}
      </h2>
      <p className="text-[#a1a1aa] max-w-2xl">
        Based on your answers, here is what we would prioritize.
      </p>

      {/* Score */}
      <div className="mt-10 border border-[#27272a] bg-[#0e0e0e] p-8 rounded-sm relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#C8A24B]" />
        <div className="flex items-baseline gap-3">
          <div className="font-heading text-6xl text-[#C8A24B]">
            {summary.score}
          </div>
          <div className="text-[#a1a1aa]">
            <div className="text-sm text-white">Presence score</div>
            <div className="text-xs">Out of 100</div>
          </div>
        </div>
        <div className="mt-4 h-1 w-full bg-[#171717] overflow-hidden">
          <div
            className="h-full bg-[#C8A24B]"
            style={{ width: `${summary.score}%` }}
          />
        </div>
      </div>

      <div className="mt-10 grid md:grid-cols-2 gap-6">
        <PanelBlock
          icon={ShieldCheck}
          title="What you already have"
          items={summary.strengths}
          emptyLabel="You are starting fresh."
          accent
        />
        <PanelBlock
          icon={AlertTriangle}
          title="Gaps to close"
          items={summary.gaps}
          emptyLabel="Nothing obvious missing."
        />
      </div>

      <div className="mt-10 border border-[#C8A24B]/40 bg-[#C8A24B]/[0.03] p-8 rounded-sm">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B] mb-3">
          Recommended Next Step
        </div>
        <div className="font-heading text-2xl md:text-3xl text-white mb-4">
          {summary.recommended_package} package
        </div>
        <div className="text-[#d4d4d8] mb-6 max-w-xl">
          Based on where you are, this is the right entry point. We will scope
          the exact deliverables around your goals during a short fit
          conversation.
        </div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a] mb-3">
          Priority Services
        </div>
        <div className="flex flex-wrap gap-2 mb-8">
          {summary.priorities.map((p) => (
            <span
              key={p}
              className="px-3 py-1.5 text-xs md:text-sm border border-[#27272a] bg-[#0a0a0a] text-[#d4d4d8] rounded-sm"
            >
              {p}
            </span>
          ))}
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to="/contact"
            data-testid="diagnostic-cta-primary"
            className="btn-gold"
          >
            Talk About My Plan <ArrowRight size={16} />
          </Link>
          <Link to="/packages" className="btn-outline">
            See Packages
          </Link>
        </div>
      </div>

      <p className="mt-10 text-xs text-[#71717a] text-center">
        Your answers are saved to your Agent Marketing account and ready when we
        speak.
      </p>
    </div>
  );
}

function PanelBlock({ icon: Icon, title, items, emptyLabel, accent }) {
  return (
    <div className="border border-[#27272a] bg-[#0e0e0e] p-6 rounded-sm">
      <div className="flex items-center gap-3 mb-4">
        <Icon
          size={16}
          className={accent ? "text-[#C8A24B]" : "text-[#a1a1aa]"}
        />
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#71717a]">
          {title}
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-[#71717a]">{emptyLabel}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => (
            <li
              key={it}
              className="flex items-center gap-2 text-sm text-[#d4d4d8]"
            >
              <span
                className={`w-1 h-1 rounded-full ${
                  accent ? "bg-[#C8A24B]" : "bg-[#52525b]"
                }`}
              />
              {it}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const inputCls =
  "w-full bg-[#0a0a0a] border border-[#27272a] rounded-sm px-4 py-3 text-white placeholder:text-[#52525b] focus:border-[#C8A24B] focus:ring-1 focus:ring-[#C8A24B] outline-none";
